import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasRole, isExternal } from '../../../../utils/roles';
import { SectionHeader } from '../../../common/SectionHeader';
import { useInvitation } from './invitationFunctions';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { selectIdentity } from '../../../../store/entities/identity';
import { EntryRequestDto } from '@api/models/invitation/EntryRequestDto';
import { CreateUpdateEntryModal, RequestEntryModal } from './RequestEntryModal';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { entryRequestService } from '@api/services/services';
import { parseISO } from 'date-fns';
import { DateFormat } from '../../../../utils/date';
import { CellDate } from '../../../common/grid/CellDate';
import { useTranslation } from 'react-i18next';
import { InnerTooltipWrapper, QcsTooltip } from '../../../common/basic/QcsTooltip';
import { QcsButton } from '../../../common/basic/QcsButton';
import { FaIcon } from '../../../common/FaIcon';
import { faCopy, faEdit, faHandshakeSimple } from '@fortawesome/free-solid-svg-icons';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import axios, { AxiosError, CancelToken } from 'axios';
import { Grid } from '../../../common/grid/Grid';
import { useAppSnackbar } from '../../../../hooks/useAppSnackbar';
import {
  entryRequestInvitationListActions,
  getEntryRequestInvitationList,
  selectEntryRequestInvitationList,
} from '../../../../store/entities/entryRequestInvitationList';
import { RequestEntryInfoModal } from '../../../entryRequest/RequestEntryInfoModal';
import { QcsCheckbox } from '../../../common/basic/QcsCheckbox';
import { QcsFormControlLabel } from '../../../common/basic/QcsFormControlLabel';
import { InvitationType } from '@api/models/enums/InvitationType';
import { UserRole } from '@api/models/enums/UserRole';
import { InvitationState } from '@api/models/enums/InvitationState';
import { doNothing } from '../../../../utils/shared';
import { useTheme } from '@mui/material';

export const InvTabEntryRequests = () => {
  const { invitation } = useInvitation();
  const identity = useAppSelector(selectIdentity);
  const isArchived = invitation.state === InvitationState.Archived;
  const canModify = hasRole(identity.roles, [
    UserRole.AdminQcs,
    UserRole.ManagerOsah,
    UserRole.ManagerWorkplace,
    UserRole.ExternalManager,
    UserRole.ExternalWorker,
    UserRole.ExternalOneTime,
  ]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { t } = useTranslation();
  const [selectedEditItem, setSelectedEditItem] = useState<CreateUpdateEntryModal | undefined>();
  const [selectedInfoItem, setSelectedInfoItem] = useState<EntryRequestDto | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [hideExpired, setHideExpired] = useState(true);
  const dispatch = useAppDispatch();
  const entryRequestList = useAppSelector(selectEntryRequestInvitationList);
  const { enqueueInfoSnackbar } = useAppSnackbar();
  const theme = useTheme();

  const handleGetData = useCallback(
    (cancelToken: CancelToken) => {
      const fromDate = hideExpired ? new Date() : undefined;
      dispatch(getEntryRequestInvitationList(cancelToken, invitation.id!, fromDate));
    },
    [dispatch, invitation.id, hideExpired],
  );

  const removeUrlParam = () => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('entryRequestId');
    const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  };

  const errorShownRef = useRef(false);

  const createSelectedItemFromEntry = (item: EntryRequestDto, excludeId = false): CreateUpdateEntryModal => ({
    id: excludeId ? undefined : item.id,
    dateFrom: item.dateFrom,
    dateTo: item.dateTo,
    timeFrom: item.timeFrom,
    establishment: item.establishment,
    supplierEmployees: item.supplierEmployees?.map((employee) => ({
      ...employee,
      id: employee.id,
      employeeId: employee.id ?? '',
      activities: [],
      workplaces: [],
    })),
    workplaces:
      item.workplaces?.map((workplace) => ({
        ...workplace,
        id: workplace.id || '',
      })) || [],
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const entryRequestId = urlParams.get('entryRequestId');

    if (entryRequestId && !errorShownRef.current) {
      setIsLoading(true);
      entryRequestService
        .getEntryRequestById(entryRequestId)
        .then((response) => {
          const item = response.data;
          if (item.dateFrom && new Date(item.dateFrom) < new Date()) {
            handleHideExpiredChange({ target: { checked: false } } as React.ChangeEvent<HTMLInputElement>);
          }
          setSelectedInfoItem(item);
          setShowInfoModal(true);
          setIsLoading(false);
          removeUrlParam();
        })
        .catch((error: AxiosError) => {
          // without !errorShownRef.current the snackbar would show multiple times
          if (error.response?.status === 404 && !errorShownRef.current) {
            errorShownRef.current = true;
            enqueueInfoSnackbar(t('invitation.entryRequest.errors.notFound'));
          }

          setIsLoading(false);
          removeUrlParam();
        });
    }
  }, []); // the list of dependencies is empty on purpose, we want this to run only once on component mount

  // Effect to handle data fetching when invitation changes
  useEffect(() => {
    if (invitation.id) {
      const cancelTokenSource = axios.CancelToken.source();
      handleRefresh(cancelTokenSource.token);

      return () => doNothing();
    }
  }, [invitation.id]);

  const isOneTime = useMemo(
    () =>
      invitation.type === InvitationType.OneTime ||
      invitation.type === InvitationType.OneTimeEntry ||
      hasRole(identity.roles, [UserRole.ExternalWorker]),
    [identity.roles, invitation.type],
  );

  const handleAddClick = () => {
    setSelectedEditItem(undefined);
    setShowAddModal(true);
  };

  const handleDuplicate = (item: EntryRequestDto) => {
    setSelectedEditItem(createSelectedItemFromEntry(item, true));
    setShowAddModal(true);
  };

  const handleDelete = (item: EntryRequestDto) => {
    setIsLoading(true);
    entryRequestService.deleteEntryRequest(item.id).then(() => {
      handleRefresh();
      setIsLoading(false);
    });
  };

  const handleRefresh = (cancelToken?: CancelToken) => {
    dispatch(entryRequestInvitationListActions.reload(true));
    handleGetData(cancelToken ?? axios.CancelToken.source().token);
  };

  const handleEdit = (item: EntryRequestDto) => {
    setSelectedEditItem(createSelectedItemFromEntry(item));
    setShowAddModal(true);
  };

  const handleShowInfo = (item: EntryRequestDto) => {
    setSelectedInfoItem(item);
    setShowInfoModal(true);
  };

  const renderData = (item: EntryRequestDto) => {
    return (
      <>
        {item.dateTo === item.dateFrom || !item.dateTo ? (
          <CellDate value={item.dateFrom} />
        ) : (
          <QcsTableCell>
            <DateFormat value={parseISO(item.dateFrom!)} showWithTime={false} /> {' - '}
            <DateFormat value={parseISO(item.dateTo)} showWithTime={false} />
          </QcsTableCell>
        )}
        <QcsTableCell>{item.establishment.name}</QcsTableCell>
        <QcsTableCell>{item.workplaces?.map((it) => it.name)?.join(', ')}</QcsTableCell>
        <QcsTableCell>{item.supplierEmployees?.map((it) => it.name + ' ' + it.lastName)?.join(', ')}</QcsTableCell>
        <QcsTableCell>{t(`invitation.entryRequest.state.${item.state}`)}</QcsTableCell>
        <QcsTableCell align="right">
          {!isExternal(identity) && !isArchived && (
            <QcsTooltip title={t('invitation.tabs.tooltips.decideEntryRequestState')} placement="top">
              <InnerTooltipWrapper>
                <QcsButton stopPropagation onClick={() => handleShowInfo(item)} disabled={isLoading}>
                  <FaIcon icon={faHandshakeSimple} color={theme.palette.common.black} />
                </QcsButton>
              </InnerTooltipWrapper>
            </QcsTooltip>
          )}
          {canModify && !isArchived && (
            <QcsTooltip title={t('invitation.tabs.tooltips.editEntryRequest')} placement="top">
              <InnerTooltipWrapper>
                <QcsButton stopPropagation onClick={() => handleEdit(item)} disabled={isLoading}>
                  <FaIcon icon={faEdit} color={theme.palette.common.black} />
                </QcsButton>
              </InnerTooltipWrapper>
            </QcsTooltip>
          )}
          {canModify && !isArchived && (
            <QcsTooltip title={t('invitation.tabs.tooltips.duplicateEntryRequest')} placement="top">
              <InnerTooltipWrapper>
                <QcsButton stopPropagation onClick={() => handleDuplicate(item)}>
                  <FaIcon icon={faCopy} color={theme.palette.common.black} />
                </QcsButton>
              </InnerTooltipWrapper>
            </QcsTooltip>
          )}
          {canModify && !isArchived && (
            <QcsTooltip title={t('invitation.tabs.tooltips.deleteEntryRequest')} placement="top">
              <InnerTooltipWrapper>
                <QcsButton stopPropagation onClick={() => handleDelete(item)} disabled={isLoading}>
                  <DeleteForeverIcon color={'error'} />
                </QcsButton>
              </InnerTooltipWrapper>
            </QcsTooltip>
          )}
        </QcsTableCell>
      </>
    );
  };

  const handleHideExpiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newHideExpired = event.target.checked;
    setHideExpired(newHideExpired);
    dispatch(entryRequestInvitationListActions.reload(true));
    const cancelTokenSource = axios.CancelToken.source();
    const fromDate = newHideExpired ? new Date() : undefined;
    dispatch(getEntryRequestInvitationList(cancelTokenSource.token, invitation.id!, fromDate));
  };

  return (
    <>
      {showAddModal && <RequestEntryModal data={selectedEditItem} onClose={() => setShowAddModal(false)} />}
      {showInfoModal && selectedInfoItem && (
        <RequestEntryInfoModal data={selectedInfoItem} onClose={() => setShowInfoModal(false)} disabled={isArchived} />
      )}
      {!isOneTime &&
        hasRole(identity.roles, [UserRole.ExternalManager, UserRole.ExternalWorker, UserRole.ManagerWorkplace, UserRole.ManagerOsah]) && (
          <SectionHeader
            title=" "
            addText="invitation.entryRequest.add"
            handleAddClick={handleAddClick}
            addDisabled={invitation.state === InvitationState.Archived}
            children={
              <QcsFormControlLabel
                control={<QcsCheckbox checked={hideExpired} onChange={handleHideExpiredChange} />}
                label={t('invitation.entryRequest.hideExpired')}
              />
            }
          />
        )}
      <Grid<EntryRequestDto>
        headers={[
          { captionStr: 'invitation.entryRequest.table.date' },
          { captionStr: 'invitation.entryRequest.table.establishment' },
          { captionStr: 'invitation.entryRequest.table.workplaces' },
          { captionStr: 'invitation.entryRequest.table.workers' },
          { captionStr: 'invitation.entryRequest.table.status' },
          {},
        ]}
        renderData={renderData}
        gridActions={entryRequestInvitationListActions}
        hidePagination
        onRowClick={handleShowInfo}
        getData={handleGetData}
        data={entryRequestList}
        search={false}
      />
    </>
  );
};
